import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { Button } from '@uhg-abyss/web/ui/Button';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Link } from '@uhg-abyss/web/ui/Link';
import { event as adobeTaggingEvent } from '@uhg-abyss/web/tools/event';
import { config } from '@uhg-abyss/web/tools/config';
import { useParams } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { tokenizer } from '@uhg-abyss/web/tools/tokenizer';

const ModalContent = styled('p', {
  margin: '10px 30px 20px 30px',
});

const ModalBody = styled('div', {
  textAlign: 'center',
});

const Title = styled('h3', {
  fontSize: 24,
  textAlign: 'center',
  paddingRight: 40,
  paddingLeft: 40,
  paddingTop: '60px',
});

const BoldText = styled('div', {
  textAlign: 'center',
  fontSize: '16px',
  fontWeight: 'bold',
  paddingBottom: '30px',
  paddingTop: '-10px',
});

const AlignButton = styled('div', {
  paddingBottom: '30px',
  marginLeft: '270px',
  marginTop: '-25px',
});

const AlignTaxIdButton = styled('div', {
  paddingBottom: '42px',
  marginLeft: '285px',
  marginTop: '-25px',
});

export const ProviderTINRegistrationModal = ({
  isOpen,
  closeModal,
  handleProviderReg,
  handleDiffTaxId,
}) => {
  const { token } = useParams();
  const parsedToken = tokenizer.parse(token);
  const isAPIMarketPlaceUser = parsedToken?.sourceSystem === 'apim';

  const handleProvider = () => {
    handleProviderReg();
    adobeTaggingEvent('MODAL_CLOSE', {
      modalName: 'tin not billing or vendor modal',
      linkName: 'go to provider registration',
    });
    closeModal();
  };

  const handleTaxId = () => {
    handleDiffTaxId();
    adobeTaggingEvent('MODAL_CLOSE', {
      modalName: 'tin not billing or vendor modal',
      linkName: 'enter billing or vendor tax id',
    });
    closeModal();
  };

  return (
    <Modal
      id="prov-tin-registration-confirmation-modal"
      isOpen={isOpen}
      showCloseButton
      style={{ padding: '50px' }}
      onClose={() => {
        adobeTaggingEvent('MODAL_CLOSE', {
          modalName: 'tin not billing or vendor modal',
          linkName: 'close',
        });
        closeModal();
      }}
      width="600px"
    >
      <Title>
        Based on the Tax ID you entered, it looks like you may be a Healthcare
        Professional or Facility.
      </Title>
      <ModalBody>
        <Modal.Section>
          {isAPIMarketPlaceUser ? (
            <ModalContent>
              It looks like the Tax ID you entered is recognized as a Healthcare
              Professional or Facility. After registering as a billing or vendor
              organization, you will have the ability to gain access to Provider
              TINs, as needed.
            </ModalContent>
          ) : (
            <ModalContent>
              It looks like the Tax ID you entered is recognized as a Healthcare
              Professional or Facility. After registering as a billing or vendor
              organization, you will have the ability to request access to
              Provider TINs, as needed, via 3rd Party Access. Visit our{' '}
              <Link
                css={{
                  'abyss-link-icon': {
                    lineHeight: 'normal',
                  },
                }}
                href={config('THIRD_PARTY_ACCESS_GUIDE')}
                variant="native"
                onClick={() => {
                  adobeTaggingEvent('DOWNLOAD_CLICKS', {
                    downloadFileName: '3rd party access guide',
                    fileUrl: config('THIRD_PARTY_ACCESS_GUIDE'),
                    fileContext: 'self paced chameleon guide',
                    location:
                      'it looks like you may be working on behalf of a healthcare professional or facility modal',
                  });
                }}
              >
                3rd Party Access Guide
              </Link>{' '}
              to learn more about the process.
            </ModalContent>
          )}
        </Modal.Section>
      </ModalBody>
      <React.Fragment>
        <BoldText>
          To continue registering as a billing or vendor organization
        </BoldText>
        <AlignButton>
          <Button onClick={handleTaxId} borderRadius="23px" color="$primary1">
            Enter billing or vendor Tax ID
          </Button>
        </AlignButton>
        <BoldText>To continue registering this Tax ID</BoldText>
        <AlignTaxIdButton>
          <Button
            onClick={handleProvider}
            borderRadius="23px"
            color="$primary1"
            variant="outline"
          >
            Go to Provider Registration
          </Button>
        </AlignTaxIdButton>
      </React.Fragment>
    </Modal>
  );
};

ProviderTINRegistrationModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  handleProviderReg: PropTypes.func.isRequired,
  handleDiffTaxId: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
};
ProviderTINRegistrationModal.defaultProps = {
  isOpen: false,
};
